import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Camera, ChevronLeft, Smile, Trash2 } from "lucide-react";
import { espaceApi, messageErreur } from "./api";
import { AVATARS, RondEleve, creerPhotos } from "./RondEleve";

/* La photo ou l'avatar d'un élève, choisi par sa famille (Jen, 02.11.2026 : « si tu appuies
   sur le L, l'option pour mettre une photo s'ouvre, ou même un avatar ? »). Tout est
   facultatif : sans rien choisir, la lettre reste. Grand chantier « photos_eleves » : fermé,
   rien de tout ceci n'apparaît (TabCours ne rend pas les ronds touchables).

   L'image se lit par la session de la famille (l'app et le serveur ne sont pas sur le même
   site : une balise <img> vers le serveur n'enverrait pas la session), en blob, une fois par
   version (`photo_v`, donnée par /moi). */
const { usePhoto, retenir } = creerPhotos((id, v) =>
  espaceApi.get(`/enfant/${encodeURIComponent(id)}/photo`, { params: { v }, responseType: "blob" })
    .then(({ data }) => URL.createObjectURL(data)));

/** Le rond d'un élève de la famille, avec sa photo quand il en a une. */
export const RondPhoto = ({ eleve, teinte, taille = 44, onClick, testId }) => {
  const photo = usePhoto(eleve?.id, eleve?.photo_v || "");
  return <RondEleve prenom={eleve?.prenom} photo={photo} avatar={eleve?.avatar} teinte={teinte}
    taille={taille} onClick={onClick} testId={testId} />;
};

/* La photo choisie, préparée ICI avant l'envoi : recadrée au carré au centre, réduite à
   256×256, en JPEG (~20 Ko). Une photo de 12 Mpx part donc en un instant, même en 4G, et le
   serveur la ré-encode encore (il ne garde jamais le fichier d'origine). */
const COTE = 256;
const lireImage = (fichier) => new Promise((ok, ko) => {
  const url = URL.createObjectURL(fichier);
  const img = new Image();
  img.onload = () => { URL.revokeObjectURL(url); ok(img); };
  img.onerror = () => { URL.revokeObjectURL(url); ko(new Error("illisible")); };
  img.src = url;
});
const toile = (cote) => {
  const c = document.createElement("canvas");
  c.width = cote; c.height = cote;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#fff";                 // une image transparente ne devient pas noire
  ctx.fillRect(0, 0, cote, cote);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  return [c, ctx];
};
export async function preparerPhoto(fichier) {
  const img = await lireImage(fichier);
  const l = img.naturalWidth; const h = img.naturalHeight;
  if (!l || !h) throw new Error("illisible");
  const carre = Math.min(l, h);
  // Une première réduction (1024 px au plus), puis par moitiés : plus net qu'un seul saut.
  let cote = Math.min(carre, 1024);
  let [c, ctx] = toile(cote);
  ctx.drawImage(img, (l - carre) / 2, (h - carre) / 2, carre, carre, 0, 0, cote, cote);
  while (cote / 2 >= COTE * 1.5) {
    const moitie = Math.round(cote / 2);
    const [c2, ctx2] = toile(moitie);
    ctx2.drawImage(c, 0, 0, cote, cote, 0, 0, moitie, moitie);
    c = c2; cote = moitie;
  }
  const [fin, ctxFin] = toile(COTE);
  ctxFin.drawImage(c, 0, 0, cote, cote, 0, 0, COTE, COTE);
  const dataUrl = fin.toDataURL("image/jpeg", 0.8);
  if (!dataUrl.startsWith("data:image/jpeg")) throw new Error("illisible");
  return dataUrl;
}

/* Une ligne de la feuille, comme une feuille d'actions d'iPhone. */
const Action = ({ Icone, children, onClick, danger = false, desactive = false, testId, htmlFor }) => {
  const classes = "w-full flex items-center justify-center gap-2 min-h-[54px] px-4 text-[16.5px] font-medium active:bg-[#F1F5F9] transition-colors disabled:opacity-40";
  const style = { color: danger ? "#DC2626" : "var(--text)" };
  const contenu = <>{Icone && <Icone size={18} style={{ color: danger ? "#DC2626" : "var(--primary)" }} />}{children}</>;
  // « Choisir une photo » est une étiquette du champ fichier : c'est ce qui ouvre le plus
  // sûrement la photothèque et l'appareil photo sur iPhone.
  if (htmlFor) {
    return <label htmlFor={htmlFor} className={`${classes} cursor-pointer`} style={style} data-testid={testId}>{contenu}</label>;
  }
  return <button type="button" onClick={onClick} disabled={desactive} className={classes} style={style} data-testid={testId}>{contenu}</button>;
};

/** La feuille qui s'ouvre quand on touche le rond d'un élève. */
export const FeuillePhoto = ({ eleve, teinte, onFermer, onChange }) => {
  const [vue, setVue] = useState("menu");      // menu | avatars | apercu
  const [apercu, setApercu] = useState("");
  const [avatar, setAvatar] = useState(eleve.avatar || "");
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState("");
  const photo = usePhoto(eleve.id, eleve.photo_v || "");
  const champ = `photo-eleve-${eleve.id}`;
  const enPlace = !!(eleve.photo_v || eleve.avatar);

  useEffect(() => {
    const echap = (e) => { if (e.key === "Escape" && !occupe) onFermer(); };
    window.addEventListener("keydown", echap);
    return () => window.removeEventListener("keydown", echap);
  }, [occupe, onFermer]);

  const surFichier = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setErreur("");
    if (f.type && !f.type.startsWith("image/")) { setErreur("Ce fichier n'est pas une photo. Choisis une image."); return; }
    setOccupe(true);
    try {
      setApercu(await preparerPhoto(f));
      setVue("apercu");
    } catch {
      setErreur("Cette photo ne peut pas être lue. Essaie avec une autre.");
    } finally {
      setOccupe(false);
    }
  };

  const envoyer = async (requete, apres) => {
    setOccupe(true);
    setErreur("");
    try {
      const { data } = await requete();
      apres?.(data);
      onChange?.({ photo_v: data.photo_v || "", avatar: data.avatar || "" });
      onFermer();
    } catch (e) {
      setErreur(messageErreur(e, "Ça n'a pas marché. Réessaie dans un instant."));
      setOccupe(false);
    }
  };
  const enregistrerPhoto = () => envoyer(() => espaceApi.post(`/enfant/${encodeURIComponent(eleve.id)}/photo`, { image: apercu }),
    // L'aperçu sert tout de suite de photo : pas de second téléchargement.
    (d) => { if (d.photo_v) retenir(eleve.id, d.photo_v, apercu); });
  const enregistrerAvatar = () => envoyer(() => espaceApi.post(`/enfant/${encodeURIComponent(eleve.id)}/avatar`, { avatar }));
  const retirer = () => envoyer(() => espaceApi.delete(`/enfant/${encodeURIComponent(eleve.id)}/photo`));

  // En tête de la feuille : ce que la tuile montrera.
  const enTete = vue === "apercu"
    ? <RondEleve prenom={eleve.prenom} photo={apercu} taille={112} />
    : vue === "avatars"
      ? <RondEleve prenom={eleve.prenom} avatar={avatar} teinte={teinte} taille={80} />
      : <RondEleve prenom={eleve.prenom} photo={photo} avatar={eleve.avatar} teinte={teinte} taille={80} />;
  const titre = vue === "apercu" ? "Aperçu" : vue === "avatars" ? "Choisir un avatar" : `Photo de ${eleve.prenom}`;

  return createPortal(
    <div className="fixed inset-0 z-[95] flex items-end justify-center"
      style={{ background: "rgba(15,27,45,0.45)", paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}
      onClick={() => { if (!occupe) onFermer(); }} data-testid="espace-photo-feuille">
      <div role="dialog" aria-modal="true" aria-label={titre} className="w-full max-w-lg px-2.5 max-h-full flex flex-col"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 10px)", color: "var(--text)" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="rounded-[18px] bg-white overflow-hidden min-h-0 flex flex-col">
          <div className="relative flex flex-col items-center text-center px-5 pt-5 pb-4 shrink-0">
            {vue !== "menu" && (
              <button type="button" onClick={() => { setVue("menu"); setErreur(""); }} disabled={occupe}
                className="absolute left-2 top-3 inline-flex items-center gap-0.5 text-[15px] font-medium min-h-[40px] px-2"
                style={{ color: "var(--primary)" }} data-testid="espace-photo-retour">
                <ChevronLeft size={18} /> Retour
              </button>
            )}
            {enTete}
            <div className="text-[17px] font-semibold mt-3" data-testid="espace-photo-titre">{titre}</div>
            {vue === "menu" && (
              <p className="text-[12.5px] text-[var(--muted)] mt-1" data-testid="espace-photo-confidentialite">
                Visible seulement par ta famille et l'école.
              </p>
            )}
            {erreur && (
              <p className="text-[13px] mt-2 rounded-xl px-3 py-2" style={{ background: "#FEF2F2", color: "#B91C1C" }}
                role="alert" data-testid="espace-photo-erreur">{erreur}</p>
            )}
          </div>

          <input id={champ} type="file" accept="image/*" className="sr-only" onChange={surFichier}
            disabled={occupe} data-testid="espace-photo-fichier" />

          {vue === "menu" && (
            <div className="divide-y border-t" style={{ borderColor: "var(--border)" }}>
              <Action Icone={Camera} htmlFor={champ} testId="espace-photo-choisir">
                {occupe ? "Un instant…" : "Choisir une photo"}
              </Action>
              <Action Icone={Smile} onClick={() => { setAvatar(eleve.avatar || ""); setVue("avatars"); }}
                desactive={occupe} testId="espace-photo-avatars">Choisir un avatar</Action>
              {enPlace && <Action Icone={Trash2} danger onClick={retirer} desactive={occupe} testId="espace-photo-retirer">Retirer</Action>}
            </div>
          )}

          {vue === "avatars" && (
            <div className="border-t overflow-y-auto overscroll-contain" style={{ borderColor: "var(--border)" }}>
              <div className="grid grid-cols-4 gap-3 px-5 py-4" data-testid="espace-photo-grille">
                {AVATARS.map((a) => {
                  const choisi = a.id === avatar;
                  return (
                    <button key={a.id} type="button" onClick={() => setAvatar(a.id)} aria-pressed={choisi}
                      aria-label={`Avatar ${a.emoji}`} data-testid={`espace-avatar-${a.id}`}
                      className="aspect-square rounded-full grid place-items-center text-[34px] transition-transform active:scale-95"
                      style={{ background: a.fond, boxShadow: choisi ? "0 0 0 3px #fff, 0 0 0 5px var(--primary)" : "none" }}>
                      <span aria-hidden="true">{a.emoji}</span>
                    </button>
                  );
                })}
              </div>
              <div className="px-5 pb-4">
                <button type="button" onClick={enregistrerAvatar} disabled={occupe || !avatar || avatar === eleve.avatar}
                  className="w-full rounded-full min-h-[50px] font-semibold text-white disabled:opacity-40"
                  style={{ background: "var(--primary)" }} data-testid="espace-photo-enregistrer">
                  {occupe ? "Un instant…" : "Enregistrer"}
                </button>
              </div>
            </div>
          )}

          {vue === "apercu" && (
            <div className="border-t px-5 py-4 grid gap-2" style={{ borderColor: "var(--border)" }}>
              <button type="button" onClick={enregistrerPhoto} disabled={occupe || !apercu}
                className="w-full rounded-full min-h-[50px] font-semibold text-white disabled:opacity-40"
                style={{ background: "var(--primary)" }} data-testid="espace-photo-enregistrer">
                {occupe ? "Un instant…" : "Enregistrer"}
              </button>
              <label htmlFor={champ} className="w-full rounded-full min-h-[46px] grid place-items-center text-[15px] font-medium cursor-pointer"
                style={{ color: "var(--primary)" }} data-testid="espace-photo-autre">Choisir une autre photo</label>
            </div>
          )}
        </div>
        <button type="button" onClick={onFermer} disabled={occupe}
          className="mt-2 w-full rounded-[18px] bg-white min-h-[56px] text-[17px] font-semibold shrink-0 active:bg-[#F1F5F9]"
          data-testid="espace-photo-annuler">Annuler</button>
      </div>
    </div>,
    document.body,
  );
};
